import { useEffect, useRef, useState } from 'react';
import CurrencySwitcher from '../CurrencySwitcher';
import { hostRoleSummary } from '../../api/host';
import HostSidebar from './HostSidebar';
import { getLocalItem, getSessionItem } from '../../utils/safeStorage';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';

const HostLayout = ({ host, onLogout, accessToken, children }) => {
  const [summary, setSummary] = useState({
    propertiesCount: 0,
    totalBookings: 0,
    upcomingArrivals: 0,
    pendingPayments: 0,
    unreadMessages: 0,
    openTickets: 0,
  });
  const logoutRef = useRef(onLogout);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [error, setError] = useState('');

  const token =
    accessToken ||
    getSessionItem('hostAccessToken') ||
    getLocalItem('hostAccessToken');

  useEffect(() => {
    logoutRef.current = onLogout;
  }, [onLogout]);

  useEffect(() => {
    if (!token) return;
    let active = true;
    const load = async () => {
      setError('');
      try {
        const data = await hostRoleSummary(token);
        if (active) setSummary(data);
      } catch (err) {
        if (!active) return;
        // Do not auto-logout on summary errors to avoid redirect loops; just surface the error.
        setError(err.message || 'Unable to load summary.');
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [token]);

  const avatarUrl = host?.profile_image_url || host?.photo_url || host?.profile_photo_url;
  const resolvedAvatar = resolveAssetUrl(avatarUrl);
  const initials = host?.full_name?.slice(0, 2)?.toUpperCase() || 'HV';

  return (
    <div className="min-h-screen bg-slate-50">
      <HostSidebar
        host={host}
        summary={summary}
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((v) => !v)}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
        onLogout={onLogout}
      />

      <main className={`transition-all duration-200 ${collapsed ? 'lg:ml-20' : 'lg:ml-72'}`}>
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white/90 px-4 backdrop-blur sm:px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white p-2 text-slate-600 shadow-sm hover:text-emerald-700 lg:hidden"
            >
              <i className="bi bi-list" />
            </button>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Homevilas</p>
              <p className="text-sm font-semibold text-slate-900">Host Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <CurrencySwitcher
              compact
              labelClassName="text-slate-500"
              selectClassName="rounded-full border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 outline-none focus:border-emerald-500"
            />
            <div className="hidden sm:block text-right">
              <p className="text-sm font-semibold text-slate-900">{host?.full_name || 'Host'}</p>
              <p className="text-xs text-slate-500">{host?.email || host?.phone || 'Logged in'}</p>
            </div>
            {resolvedAvatar ? (
              <img
                src={resolvedAvatar}
                alt={host?.full_name || 'Host'}
                className="h-10 w-10 rounded-full border border-emerald-100 object-cover"
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-800 font-semibold">
                {initials}
              </div>
            )}
          </div>
        </header>

        <div className="p-4 sm:p-6 lg:p-8">
          {error && (
            <div className="mb-4 rounded-xl border border-rose-100 bg-rose-50 px-4 py-2 text-sm text-rose-700">
              {error}
            </div>
          )}
          {children}
        </div>
      </main>
    </div>
  );
};

export const HostSection = ({ title, description, action, children }) => (
  <div className="max-w-6xl mx-auto space-y-3">
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
        {description && <p className="text-sm text-slate-600">{description}</p>}
      </div>
      {action}
    </div>
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      {children || (
        <div className="p-6 text-sm text-slate-600">This section is coming soon.</div>
      )}
    </div>
  </div>
);

export default HostLayout;
